import { searchConditionalTagResults } from "../../services/taggingResultsService";
import { downloadStyledExcel } from "../../utils/downloadStyledExcel";
import { buildTaggingResultRowId } from "./constants";
import {
  exportScopeLabel,
  type TaggingResultExportLabels,
  type TaggingResultFilter,
  type TaggingResultPageQuery,
} from "./taggingResultData";

export type ConditionalTaggingStatus = "inProgress" | "complete";

export type ConditionalTaggingResultRow = {
  id: string;
  objectId: string;
  objectType: string;
  objectTypeName: string;
  objectSubType: string;
  objectSubTypeName: string;
  conceptCodes: string[];
  title: string;
  version: string;
  taggingResults: string;
  status: ConditionalTaggingStatus;
  /** Object detail URL from the search item `object_id_link` field. */
  link?: string;
};

type ConditionalTaggingBaseRow = Omit<
  ConditionalTaggingResultRow,
  "objectType" | "objectTypeName" | "objectSubType" | "objectSubTypeName"
>;

const BASE_ROWS: ConditionalTaggingBaseRow[] = [
  {
    id: "INS28174",
    title: "New Regulation 0311",
    version: "1.0",
    taggingResults: "-",
    status: "inProgress",
    conceptCodes: [],
  },
  {
    id: "INS28171",
    title: "New Regulation 0311 02",
    version: "3.0",
    taggingResults: "High Priority, Regulatory Affairs",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS28159",
    title: "Test Regulation 12345",
    version: "2.0",
    taggingResults: "High Priority, Quality",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS28827",
    title: "for version compare used data",
    version: "1.0",
    taggingResults: "High Priority, Manufacturing",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS14568",
    title: "March record with Newsletter new logic",
    version: "3.0",
    taggingResults: "High Priority, Regulatory Affairs",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS49906",
    title: "for compare data-ing",
    version: "1.0",
    taggingResults: "High Priority, Quality",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS48170",
    title: "api test data",
    version: "1.0",
    taggingResults: "-",
    status: "inProgress",
    conceptCodes: [],
  },
  {
    id: "INS49846",
    title: "New Regulation 03846",
    version: "1.0",
    taggingResults: "Medium Priority, Quality",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
  {
    id: "INS11118",
    title: "different language test file",
    version: "1.0",
    taggingResults: "-",
    status: "inProgress",
    conceptCodes: [],
  },
  {
    id: "INS49554",
    title: "two file for compare",
    version: "2.0",
    taggingResults: "-",
    status: "inProgress",
    conceptCodes: [],
  },
  {
    id: "INS49505",
    title: "New Regulation 9505",
    version: "1.0",
    taggingResults: "Low, Regulatory Affairs",
    status: "complete",
    conceptCodes: ["Priority", "Department"],
  },
];

export const MOCK_CONDITIONAL_TAGGING_RESULTS: ConditionalTaggingResultRow[] =
  BASE_ROWS.map((row) => ({
    ...row,
    objectId: row.id,
    objectType: "insight",
    objectTypeName: "Insight",
    objectSubType: "regulation",
    objectSubTypeName: "Regulation",
  }));

export function filterConditionalTaggingResults(
  filter: TaggingResultFilter,
): ConditionalTaggingResultRow[] {
  const objectType = filter.objectType.trim().toLowerCase();
  const objectSubType = filter.objectSubType.trim().toLowerCase();
  const conceptCode = filter.conceptCode.trim().toLowerCase();

  return MOCK_CONDITIONAL_TAGGING_RESULTS.filter(
    (row) =>
      (!objectType || row.objectType.toLowerCase() === objectType) &&
      (!objectSubType ||
        row.objectSubType.toLowerCase() === objectSubType) &&
      (!conceptCode ||
        row.conceptCodes.some(
          (code) => code.toLowerCase() === conceptCode,
        )),
  );
}

export type ConditionalTaggingResultPage = Readonly<{
  rows: ConditionalTaggingResultRow[];
  total: number;
}>;

export async function fetchConditionalTaggingResultPage(
  query: TaggingResultPageQuery,
): Promise<ConditionalTaggingResultPage> {
  const page = await searchConditionalTagResults({
    objectType: query.objectType,
    objectSubType: query.objectSubType,
    conceptCode: query.conceptCode,
    conceptValue: query.conceptValue,
    objectId: query.objectId,
    pageIndex: query.pageIndex,
    pageSize: query.pageSize,
  });
  return {
    rows: page.items.map((item) => ({
      id: buildTaggingResultRowId(item.id, item.version),
      objectId: item.id,
      objectType: item.objectType,
      objectTypeName: item.objectTypeName,
      objectSubType: item.objectSubType,
      objectSubTypeName: item.objectSubTypeName,
      conceptCodes: item.conceptCodes,
      title: item.title,
      version: item.version,
      taggingResults: item.taggingResults || "-",
      status: item.status === "complete" ? "complete" : "inProgress",
      link: item.link ?? "",
    })),
    total: page.total,
  };
}

export function downloadConditionalTaggingResultsExcel(
  rows: ConditionalTaggingResultRow[],
  labels: TaggingResultExportLabels = {},
): Promise<void> {
  return downloadStyledExcel({
    entityName: "Conditional Classification Result",
    sheetName: "Conditional Classification Result",
    columns: [
      { header: "Object Type", width: 16 },
      { header: "Object Sub-Type", width: 18 },
      { header: "Object ID", width: 16 },
      { header: "Title", width: 40 },
      { header: "Version", width: 12 },
      { header: "Classification Results", width: 36 },
    ],
    rows: rows.map((row) => [
      exportScopeLabel(labels.objectTypeLabel, row.objectTypeName),
      exportScopeLabel(labels.objectSubTypeLabel, row.objectSubTypeName),
      row.objectId,
      row.title,
      row.version,
      row.taggingResults,
    ]),
  });
}
